<template>
  <div
    :style="cssVars"
    class="file-content-container mt-10"
  >
    <v-card
      class="login-card"
      data-cy="login-header-card"
      :class="($vuetify.breakpoint.smAndDown) ? 'sm' : ''"
    >
      <div
        v-if="apiClient == null || orgs == null"
        style="padding-bottom: 10px;"
      >
        <sn-loader
          :loading="true"
          :wait-for-animation="true"
          class="pt-8"
        />
      </div>
      <template v-else>
        <v-row align="center" justify="center" class="mb-6">
          <v-img
            :src="logoNoText"
            max-width="71"
            data-cy="logo-no-text"
          />
          <v-icon
            class="mx-6"
            width="60"
          >
            $snTabArrows
          </v-icon>
          <v-img
            v-if="apiClientLogo"
            :src="apiClientLogo"
            max-width="71"
            data-cy="logo-no-text"
          />
          <span
            v-else class="sn-subtitle-1 no-logo"
          >
            {{ apiClient.name }}
          </span>
        </v-row>

        <v-row align="center" justify="center" class="mb-6">
          <div
            class="sn-h2"
            data-cy="title"
            :style="'text-align: center'"
          >
            Connect nCino eSign to {{ apiClient.name }}
          </div>

          <sn-autocomplete
            v-if="orgs.length > 1"
            v-model="selectedMersOrgId"
            placeholder="Select an organization"
            :items="orgs"
            :required="true"
            label="Select which organization to connect:"
            item-text="optionLabel"
            item-value="guid"
            data-cy="mers-org-selector"
            :disabled="false"
            :hide-details="true"
            attach
            class="org-selector"
          />

          <div v-else>
            {{ orgs[0].optionLabel }}
          </div>
        </v-row>

        <v-row class="mb-6">
          <div class="pt-6 pb-7 px-4 permissions">
            <span class="sn-subtitle-2 mb-4">Allow {{ apiClient.name }} to perform the following:</span>
            <ul>
              <li
                v-for="permission in consentPermissions"
                :key="permission"
                class="sn-body-2"
              >
                {{ permission }}
              </li>
            </ul>
          </div>
        </v-row>

        <v-row align="center" justify="center" class="mb-6 button-row">
          <sn-btn
            type="primary"
            class="connect mb-4"
            aria-label="connect"
            data-cy="connect"
            size="large"
            text="Connect"
            :loading="loading"
            :disabled="selectedMersOrgId == null"
            @click="authorize"
          />
          <sn-btn
            type="secondary"
            class="cancel"
            aria-label="cancel"
            data-cy="cancel"
            size="large"
            text="Cancel"
            :disabled="loading"
            @click="cancelConsent"
          />
        </v-row>
      </template>
    </v-card>
  </div>
</template>

<script>

import logoNoText from '~/assets/images/logos/ncino_logo_no_text_color.png'
import { apiClient, authorizeApiClient } from './graphql/api_client_queries.gql'
import { orgs } from './graphql/signing_room_queries.gql'
import { mapGetters, mapMutations } from 'vuex'
import SnLoader from '~/components/common/snLoader'

export default {
  name: 'Consent',
  components: {
    SnLoader
  },
  data () {
    return {
      loading: false,
      logoNoText: logoNoText,
      clientId: null,
      redirectUri: null,
      state: null,
      orgs: null,
      selectedMersOrgId: null,
      apiClient: null,
      consentPermissions: null
    }
  },
  computed: {
    ...mapGetters(['user']),
    contentMaxWidth () {
      if (this.$vuetify.breakpoint.mdAndDown) {
        return '100%'
      } else {
        return '560px'
      }
    },
    cssVars () {
      return {
        '--content-max-width': this.contentMaxWidth
      }
    },
    apiClientLogo () {
      const imgString = this.apiClient.logoAsBase64
      if (imgString) {
        return `data:image/png;base64,${imgString}`
      }
      return undefined
    }
  },
  mounted () {
    this.clientId = this.$route.params.guid
    this.redirectUri = this.$route.query.redirect_uri
    this.state = this.$route.query.state

    this.getApiClient()
    this.getOrgs()
  },
  methods: {
    ...mapMutations(['setHeaderText', 'setSubtitleText', 'setBreadcrumbs', 'setHeaderTabs', 'setSuccessToast', 'setErrorToastType', 'setActiveHeaderTab']),
    getApiClient () {
      return this.$apollo.query({
        query: apiClient,
        variables: {
          clientId: this.$route.params.guid,
          runInquiry: true
        },
        fetchPolicy: 'no-cache'
      }).then(({ data }) => {
        this.apiClient = data.apiClient
        this.consentPermissions = JSON.parse(this.apiClient.consentPermissions)
        return this.apiClient
      }).catch((err) => {
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
      })
    },

    getOrgs () {
      return this.$apollo.query({
        query: orgs,
        variables: {
          companyGuid: null,
          order: 'remote_id ASC',
          active: true
        }
      }).then(({ data }) => {
        this.orgs = data.orgs.nodes
        this.orgs.forEach(org => { org.optionLabel = org.remoteId ? `${org.remoteId} ${org.name}` : org.name })

        if (this.orgs.length === 1) {
          this.selectedMersOrgId = this.orgs[0].guid
        }

        return this.orgs
      }).catch((err) => {
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
      })
    },

    cancelConsent () {
      sessionStorage.clear()
      window.location.href = `${this.redirectUri}?state=${this.state}`
    },

    authorize () {
      this.loading = true
      this.$apollo.mutate({
        mutation: authorizeApiClient,
        variables: {
          orgGuid: this.selectedMersOrgId,
          apiClientGuid: this.apiClient.guid
        }
      }).then(({ data }) => {
        this.loading = false
        if (data.authorizeApiClient.error) {
          this.setErrorToastType({ description: data.authorizeApiClient.error })
        } else {
          const guid = data.authorizeApiClient.guid
          window.location.href = `${this.redirectUri}?code=${guid}&state=${this.state}`
        }
      }).catch(err => {
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';
.button-row {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  .connect,
  .cancel {
    width: 100%;
  }
}

.org-selector {
  margin-top: 12px;
  width: 100%;
}

.file-content-container {
  margin: 0 auto;
  padding: 0 16px;
  max-width: var(--content-max-width);
}

::v-deep .v-avatar .v-image {
  border-radius: 0;
}

.login-card.sm {
  position: relative;
  top: 10px;
  border: none;
  box-shadow: none;
  width: 100vw;
  padding: 0;
  border-radius: 6px;
}

.login-card:not(.sm) {
  position: relative;
  top: -155px;
  padding: 56px;
  border-radius: 6px;
}

.permissions {
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 8px;
  background-color: $sn-gray-01;
}

.no-logo {
  display: inline-block;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
